import { useState, useEffect, useMemo } from "react";
import PageLayout from "../../../Layouts/PageLayout/PageLayout";
import BarChartGraph from "../../../components/common/Charts/BarChartGraph";
import Table from "../../../components/common/Table/Table";
import Button from "../../../components/common/Button/Button";
import { FiDownload } from "react-icons/fi";
import { MdOutlineFileDownload } from "react-icons/md";
import { getApiInstance } from "../../../utils/api";
import {
    useExcelExport,
    codsFilaDe,
    zonaExportFromFilterState,
} from "../useExcelExport";
import TabsNavigation from "../../../components/common/TabsNavigation/TabsNavigation";
import Modal from "../../../components/common/Modal/Modal";
import { FilterBar } from "../../../components/filtersNew/filter-barNew";
import ExcluirRaeeZeroWasteToggle, { filtrarProyectosExcluidos } from "../../../components/common/Inputs/ExcluirRaeeZeroWasteToggle/ExcluirRaeeZeroWasteToggle";
import { Field } from "../../../components/ui-kitNew/controlsNew";
import { Select } from "../../../components/ui-kitNew/selectNew";
import {
    createFilterState,
    applyFilterState,
} from "../../../lib/reportNew/filter-stateNew";
import { ZONE_LABEL_MAP } from "../../../constants/zones";
import "../cantidadesGeneradas/CantidadesGeneradas.scss";

export default function SegregacionValorizacion() {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [informeData, setInformeData] = useState([]);
    const [activeTab, setActiveTab] = useState("grafica");

    const [tipoDesglose, setTipoDesglose] = useState("cadena");

    const filterFields = [
        {
            key: "rangoFechas",
            label: "Rango de fechas",
            type: "dateRange",
            rangeEndField: "fechaTratamiento",
        },
        {
            key: "codUbicacion",
            label: "Ubicación",
            type: "enum",
            labelMap: ["", "Calle", "Centro comercial"],
        },
        { key: "intermediario", label: "Intermediario", type: "enum" },
        { key: "provincia", label: "Provincia", type: "enum" },
        { key: "cadena", label: "Cadena", type: "enum" },
        { key: "tipoObra", label: "Tipo de obra", type: "enum" },
        { key: "pais", label: "País", type: "enum" },
        {
            key: "codZona",
            label: "Zona",
            type: "enum",
            labelMap: ZONE_LABEL_MAP,
        },
    ];

    const defaultQuickFilters = [
        "rangoFechas",
        "codUbicacion",
        "intermediario",
        "provincia",
        "codZona",
    ];

    const [filterState, setFilterState] = useState(() =>
        createFilterState(defaultQuickFilters)
    );

    const { isModalExcelOpen, openModal, closeModal, handleExportToExcel, handleExportInformeCompleto, exportando } =
        useExcelExport({
            onError: setError,
            resumenUrl:
                "api/Informe/CreateReportExcelSegregacionValorizacion",
        });

    const zonaExport = useMemo(
        () => zonaExportFromFilterState(filterState),
        [filterState]
    );

    const opcionesDesglose = [
        { value: "cadena", label: "Por Cadena" },
        { value: "tipoObra", label: "Por Tipo de obra" },
        { value: "pais", label: "Por País" },
    ];

    useEffect(() => {
        const fetchInformeSegregacion = async () => {
            setLoading(true);
            try {
                const api = getApiInstance();
                const response = await api.get(
                    `/api/Informe/GetSegregacion`
                );
                setInformeData(
                    Array.isArray(response.data) ? response.data : []
                );
                setError(null);
            } catch (err) {
                console.error(err);
                setError("No se han podido cargar los datos.");
            } finally {
                setLoading(false);
            }
        };
        fetchInformeSegregacion();
    }, []);

    const [excluirRaeeZeroWaste, setExcluirRaeeZeroWaste] = useState(false);

    // --- LÓGICA CENTRAL REFACTORIZADA CON useMemo ---
    // El backend devuelve una fila por residuo; aquí se agrupa por la
    // dimensión seleccionada y se calculan los porcentajes.
    const datosFiltrados = useMemo(
        () =>
            filtrarProyectosExcluidos(
                applyFilterState(informeData, filterState, filterFields),
                excluirRaeeZeroWaste
            ),
        [informeData, filterState, filterFields, excluirRaeeZeroWaste]
    );

    const { datosGrafico, datosTabla, categoriaLabel } = useMemo(() => {
        const config = {
            cadena: { key: "cadena", label: "Cadena" },
            tipoObra: { key: "tipoObra", label: "Tipo de Obra" },
            pais: { key: "pais", label: "País" },
        }[tipoDesglose];

        const filas = Array.isArray(datosFiltrados) ? datosFiltrados : [];

        // 1. Agrupa por la dimensión seleccionada
        const agrupado = filas.reduce((acc, item) => {
            const groupName = item[config.key] ?? "Sin categoría";
            if (!acc[groupName]) {
                acc[groupName] = {
                    codObras: new Set(),
                    pesoTotal: 0,
                    pesoValorizado: 0,
                    pesoSegregado: 0,
                };
            }

            const group = acc[groupName];
            const peso = Number(item.peso) || 0;
            const esSegrega =
                item.segrega === 1 || item.segrega === true;

            group.codObras.add(item.codObra);
            group.pesoTotal += peso;
            group.pesoValorizado += Number(item.valorizacion) || 0;
            if (esSegrega) group.pesoSegregado += peso;

            return acc;
        }, {});

        // 2. Calcula los porcentajes por categoría
        const tablaProcesada = Object.keys(agrupado).map((categoria) => {
            const group = agrupado[categoria];
            const porcentajeValorizacion =
                group.pesoTotal > 0
                    ? (100 * group.pesoValorizado) / group.pesoTotal
                    : 0;
            const porcentajeSegregacion =
                group.pesoTotal > 0
                    ? (100 * group.pesoSegregado) / group.pesoTotal
                    : 0;

            return {
                categoria,
                numObras: group.codObras.size,
                porcentajeValorizacion,
                porcentajeSegregacion,
            };
        });

        // 3. Formatea los datos para el gráfico
        const graficoProcesado = tablaProcesada.map((item) => ({
            name: item.categoria,
            // Las claves deben coincidir con las series del gráfico
            Valorización: parseFloat(
                item.porcentajeValorizacion.toFixed(4)
            ),
            Segregación: parseFloat(item.porcentajeSegregacion.toFixed(4)),
        }));

        return {
            datosGrafico: graficoProcesado,
            datosTabla: tablaProcesada,
            categoriaLabel: config.label,
        };
    }, [datosFiltrados, tipoDesglose]);

    // Las columnas de la tabla usan la etiqueta dinámica
    const columnasTabla = [
        {
            key: "categoria",
            label: categoriaLabel,
            visible: true,
            sortable: true,
        },
        {
            key: "numObras",
            label: "Nº Obras",
            visible: true,
            sortable: true,
            type: "number",
        },
        {
            key: "porcentajeValorizacion",
            label: "% Valorización",
            visible: true,
            sortable: true,
            type: "number",
            render: (row) => `${row.porcentajeValorizacion.toFixed(4)} %`,
        },
        {
            key: "porcentajeSegregacion",
            label: "% Segregación",
            visible: true,
            sortable: true,
            type: "number",
            render: (row) => `${row.porcentajeSegregacion.toFixed(4)} %`,
        },
    ];

    const tabs = [
        { id: "grafica", label: "Gráfica" },
        { id: "listado", label: "Listado" },
    ];

    // Renderizado del contenido del gráfico
    const renderGraficaContent = () => (
        <div className="cantidades-generadas-content">
            <div className="graficas-card">
                <div className="charts-section">
                    <div className="chart-container bar-chart-container">
                        <div className="chart-header">
                            <h3>Segregación y Valorización</h3>
                            {/* <span className="total-amount">
                <strong>Real:</strong> {formatCurrency(totalCosteReal)}
              </span>
              <span className="total-amount">
                <strong>Previsto:</strong> {formatCurrency(totalCostePrevisto)}
              </span> */}
                        </div>
                        <div className="chart-wrapper">
                            <BarChartGraph
                                data={{ all: datosGrafico }}
                                activeTab="all"
                                names={{
                                    Valorización: "% Valorización",
                                    Segregación: "% Segregación",
                                }}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    // Renderizado del contenido de la tabla
    const renderListadoContent = () => (
        <div className="listado-content">
            <Table
                columns={columnasTabla}
                dataTable={datosTabla}
                keyProp="categoria"
                sortProp="categoria"
                hideFilterBar
                searchTerms={[
                    "categoria",
                    "numObras",
                    "porcentajeValorizacion",
                    "porcentajeSegregacion",
                ]}
                noResultsMessage="No hay datos para el desglose seleccionado."
                elementName={categoriaLabel.toLowerCase()}
            />
        </div>
    );

    return (
        <PageLayout
            title="Informe de Segregación y Valorización"
            subtitle="Análisis de porcentajes por diferentes criterios de desglose"
            actions={[
                    <Button
                        key="export"
                        componentType="button"
                        variant="filled"
                        startIcon={<FiDownload />}
                        onClick={openModal}
                    >
                        Exportar a excel
                    </Button>,
                ]}
        >
            <TabsNavigation
                tabs={tabs}
                activeTab={activeTab}
                onTabChange={setActiveTab}
            />

            <div className="filters-section-common">
                <FilterBar
                    rows={informeData}
                    state={filterState}
                    onChange={setFilterState}
                    resultCount={datosFiltrados.length}
                    totalCount={informeData.length}
                    fields={filterFields}
                    headerExtra={
                        <div className="flex items-end gap-8">
                            <Field label="Agrupar por">
                                <Select
                                    options={opcionesDesglose}
                                    value={tipoDesglose}
                                    onChange={setTipoDesglose}
                                    placeholder="Agrupar por"
                                    triggerClassName="!h-[40px] px-3 !text-[15px]"
                                />
                            </Field>
                            <ExcluirRaeeZeroWasteToggle
                                activo={excluirRaeeZeroWaste}
                                onChange={setExcluirRaeeZeroWaste}
                            />
                        </div>
                    }
                    excludeFields={["codTipoObra", "codCadena","codIntermediario", "ubicacion", "codProvincia", "codPais", "codFila", "numObras"]}
                />
            </div>

            {error && <div className="error-message">{error}</div>}

            <Modal
                isOpen={isModalExcelOpen}
                onClose={closeModal}
                title="Exportar informe a Excel"
                actions={[
                    <Button
                        key="export-resumen"
                        className="export-excel__btn export-excel__btn--primary"
                        onClick={() =>
                            handleExportToExcel("resumen", {
                                tipoDesglose,
                                ...zonaExport,
                                codsFila: codsFilaDe(datosFiltrados),
                            })
                        }
                        startIcon={<MdOutlineFileDownload />}
                        disabled={exportando}
                    >
                        {exportando === "resumen"
                            ? "Generando..."
                            : "Informe Segregación y Valorización"}
                    </Button>,
                    <Button
                        key="export-completo"
                        className="export-excel__btn export-excel__btn--secondary"
                        onClick={() =>
                            handleExportInformeCompleto({ filterState, excluirRaeeZeroWaste })
                        }
                        startIcon={<MdOutlineFileDownload />}
                        disabled={exportando}
                    >
                        {exportando === "completo"
                            ? "Generando..."
                            : "Informe Completo"}
                    </Button>,
                ]}
            >
                <div className="export-excel__actions"></div>
            </Modal>

            {loading ? (
                activeTab === "grafica" ? (
                    <div
                        className="informe-chart-skeleton"
                        aria-hidden="true"
                    >
                        <div className="informe-chart-skeleton__donut" />
                        <div className="informe-chart-skeleton__lines">
                            <div className="informe-chart-skeleton__line" />
                            <div className="informe-chart-skeleton__line informe-chart-skeleton__line--medium" />
                            <div className="informe-chart-skeleton__line informe-chart-skeleton__line--short" />
                        </div>
                    </div>
                ) : (
                    <div
                        className="informe-table-skeleton"
                        aria-hidden="true"
                    >
                        <div className="informe-table-skeleton__row" />
                        <div className="informe-table-skeleton__row" />
                        <div className="informe-table-skeleton__row" />
                        <div className="informe-table-skeleton__row" />
                    </div>
                )
            ) : activeTab === "grafica" ? (
                renderGraficaContent()
            ) : (
                renderListadoContent()
            )}
        </PageLayout>
    );
}
